import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, Eye, FileText, Trash2 } from 'lucide-react'
import { DocumentUpload, DocumentViewer } from '../documents'
import type { EntityDocument } from '../documents/DocumentList'
import {
  Accordion,
  AccordionItem,
  Checkbox,
  Label,
  Badge,
  ShadcnButton,
} from '../ui'
import { apiService } from '../../services/api'
import type {
  CustomerDocumentKind,
  SaleDocumentationChecklistKey,
  SaleDocumentationChecklistMeta,
  SaleDocumentationChecklistRow,
} from '../../types'

const CHECKLIST_ORDER: SaleDocumentationChecklistKey[] = [
  'buyer_id',
  'address_proof',
  'atpv_signed',
  'inspection_done',
  'transfer_fee_paid',
  'sent_to_dispatcher',
  'invoice_issued',
]

const VEHICLE_MANDATORY_SLOTS: { type: string; labelKey: string }[] = [
  { type: 'rg_cpf', labelKey: 'slotRgCpf' },
  { type: 'cnpj', labelKey: 'slotCnpj' },
  { type: 'articles_of_incorporation', labelKey: 'slotArticles' },
  { type: 'responsible_id_doc', labelKey: 'slotResponsible' },
  { type: 'address_proof', labelKey: 'slotAddressProof' },
  { type: 'atpv_signed', labelKey: 'slotAtpv' },
  { type: 'inspection_report', labelKey: 'slotInspection' },
  { type: 'payment_fees', labelKey: 'slotPaymentFees' },
  { type: 'debts_clearance', labelKey: 'slotDebts' },
  { type: 'transfer_fee', labelKey: 'slotTransferFee' },
  { type: 'dispatcher_proof', labelKey: 'slotDispatcherProof' },
]

const OPTIONAL_UPLOAD_SLOT_TYPES = new Set([
  'atpv_signed',
  'inspection_report',
  'payment_fees',
  'debts_clearance',
  'transfer_fee',
  'dispatcher_proof',
])

type SaleDocumentationChecklistSectionProps = {
  saleId: string
  saleEntityDocuments: EntityDocument[]
  checklist: SaleDocumentationChecklistRow[] | undefined
  checklistMeta?: SaleDocumentationChecklistMeta
  canEdit: boolean
  onRefresh: () => void | Promise<void>
  onToast?: (message: string, type: 'success' | 'error') => void
}

function visibleSlotsForCustomer(documentKind: CustomerDocumentKind) {
  const pjOnly = new Set(['cnpj', 'articles_of_incorporation', 'responsible_id_doc'])
  const pfOnly = new Set(['rg_cpf'])
  if (documentKind === 'cnpj') {
    return VEHICLE_MANDATORY_SLOTS.filter((s) => !pfOnly.has(s.type))
  }
  return VEHICLE_MANDATORY_SLOTS.filter((s) => !pjOnly.has(s.type))
}

function entityDocsForSlot(docs: EntityDocument[], slotType: string): EntityDocument[] {
  if (slotType === 'inspection_report') {
    return docs.filter((d) => d.type === 'inspection_report' || d.type === 'inspection')
  }
  return docs.filter((d) => d.type === slotType)
}

export const SaleDocumentationChecklistSection: React.FC<SaleDocumentationChecklistSectionProps> = ({
  saleId,
  saleEntityDocuments,
  checklist,
  checklistMeta,
  canEdit,
  onRefresh,
  onToast,
}) => {
  const { t } = useTranslation()
  const [togglingKey, setTogglingKey] = useState<SaleDocumentationChecklistKey | null>(null)
  const [selectedDocument, setSelectedDocument] = useState<EntityDocument | null>(null)

  const byKey = new Map((checklist ?? []).map((r) => [r.key, r]))
  const customerKind = checklistMeta?.customerDocumentType ?? 'cpf'
  const corporatePjComplete = checklistMeta?.corporatePjComplete ?? true

  const visibleSlots = useMemo(() => visibleSlotsForCustomer(customerKind), [customerKind])

  const handleChecklistChange = useCallback(
    async (key: SaleDocumentationChecklistKey, checked: boolean) => {
      if (!canEdit || key === 'invoice_issued') return
      try {
        setTogglingKey(key)
        await apiService.patchSaleDocumentationChecklist(saleId, key, { completed: checked })
        await onRefresh()
        onToast?.(
          checked
            ? t('financialSales.saleDocumentation.checklistSaved')
            : t('financialSales.saleDocumentation.checklistCleared'),
          'success',
        )
      } catch {
        onToast?.(t('financialSales.saleDocumentation.checklistError'), 'error')
      } finally {
        setTogglingKey(null)
      }
    },
    [canEdit, onRefresh, onToast, saleId, t],
  )

  const handleRemoveSaleDoc = useCallback(
    async (docId: string) => {
      if (!canEdit) return
      try {
        await apiService.deleteEntityDocument(docId)
        await onRefresh()
      } catch {
        onToast?.(t('financialSales.saleDocumentation.deleteError'), 'error')
      }
    },
    [canEdit, onRefresh, onToast, t],
  )

  const checklistHeader = (
    <div className="flex flex-wrap items-center gap-2">
      <span>{t('financialSales.saleDocumentation.documentationAccordionChecklist')}</span>
      <Badge variant="gray" className="text-[0.65rem] font-normal">
        {CHECKLIST_ORDER.length}
      </Badge>
    </div>
  )

  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">
        {t('financialSales.saleDocumentation.documentationAccordionUploadsHint')}
      </p>
      <Accordion>
      <AccordionItem title={checklistHeader} defaultOpen>
        <p className="text-xs text-muted-foreground mb-3">
          {t('financialSales.saleDocumentation.checklistUploadRequiredHint')}
        </p>
        <ul className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
          {CHECKLIST_ORDER.map((key) => {
            const row = byKey.get(key)
            const done = Boolean(row?.completedAt)
            const canToggleOn = done || row?.hasBackingDocument === true
            const disabled =
              !canEdit ||
              key === 'invoice_issued' ||
              togglingKey === key ||
              !canToggleOn
            const wizardRequired = row?.wizardRequired === true
            const recommended = row?.recommended === true
            return (
              <li key={key} className="flex items-start gap-3">
                <Checkbox
                  id={`doc-check-sale-${key}`}
                  checked={done}
                  disabled={disabled}
                  onCheckedChange={(v) => void handleChecklistChange(key, v === true)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Label
                      htmlFor={`doc-check-sale-${key}`}
                      className="text-sm font-normal cursor-pointer leading-snug"
                    >
                      {t(`financialSales.saleDocumentation.checklistKeys.${key}`)}
                    </Label>
                    {wizardRequired && (
                      <Badge variant="primary" className="text-[0.65rem]">
                        {t('financialSales.saleDocumentation.checklistBadgeRequired')}
                      </Badge>
                    )}
                    {recommended && (
                      <Badge variant="gray" className="text-[0.65rem]">
                        {t('financialSales.saleDocumentation.checklistBadgeRecommended')}
                      </Badge>
                    )}
                  </div>
                  {key === 'invoice_issued' && (
                    <p className="text-xs text-muted-foreground">
                      {t('financialSales.saleDocumentation.invoiceSyncedHint')}
                    </p>
                  )}
                  {key === 'atpv_signed' && (
                    <p className="text-xs text-muted-foreground">
                      {t('financialSales.saleDocumentation.checklistAtpvHint')}
                    </p>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
        {customerKind === 'cnpj' && !corporatePjComplete && (
          <p className="text-sm text-amber-800 dark:text-amber-200 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 mt-4">
            {t('financialSales.saleDocumentation.pjCorporateBanner')}
          </p>
        )}
      </AccordionItem>

      {visibleSlots.map((slot) => {
        const slotDocs = entityDocsForSlot(saleEntityDocuments, slot.type)
        const label = t(`financialSales.saleDocumentation.${slot.labelKey}`)
        const optional = OPTIONAL_UPLOAD_SLOT_TYPES.has(slot.type)
        const slotHeader = (
          <div className="flex flex-wrap items-center gap-2 pr-2">
            <span>{label}</span>
            {optional && (
              <span className="text-xs font-normal text-muted-foreground">
                ({t('financialSales.saleDocumentation.slotOptionalHint')})
              </span>
            )}
            <Badge variant={slotDocs.length > 0 ? 'primary' : 'gray'} className="text-[0.65rem]">
              {t('financialSales.saleDocumentation.slotFileCount', { count: slotDocs.length })}
            </Badge>
          </div>
        )
        return (
          <AccordionItem key={slot.type} title={slotHeader} defaultOpen={slotDocs.length > 0}>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              {t('financialSales.saleDocumentation.slotUploadedListLabel')}
            </p>
            {slotDocs.length > 0 ? (
              <ul className="space-y-2 mb-4">
                {slotDocs.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="text-sm truncate">{doc.name}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {doc.url ? (
                        <ShadcnButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => setSelectedDocument(doc)}
                        >
                          <Eye className="h-4 w-4" />
                        </ShadcnButton>
                      ) : null}
                      {doc.url ? (
                        <ShadcnButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </ShadcnButton>
                      ) : null}
                      {canEdit ? (
                        <ShadcnButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-destructive hover:text-destructive"
                          onClick={() => void handleRemoveSaleDoc(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </ShadcnButton>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground mb-4">
                {t('financialSales.saleDocumentation.slotNoFilesYet')}
              </p>
            )}
            {canEdit ? (
              <DocumentUpload
                entityType="sale"
                entityId={saleId}
                saleId={saleId}
                documentType={slot.type}
                label={t('financialSales.saleDocumentation.uploadSlot')}
                onUploadSuccess={() => void onRefresh()}
                onUploadError={(msg) => onToast?.(msg, 'error')}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('financialSales.saleDocumentation.readOnlyHint')}
              </p>
            )}
          </AccordionItem>
        )
      })}
    </Accordion>
    {selectedDocument ? (
      <DocumentViewer
        url={selectedDocument.url}
        name={selectedDocument.name}
        isOpen={true}
        onClose={() => setSelectedDocument(null)}
      />
    ) : null}
    </div>
  )
}
